import { ref } from 'yup';
import { yupAddress, yupBoolean, yupObject, yupString } from '../../yup';
import { NextOfKinRelationship } from '@/helpers/const';
import type { NextOfKinEntry } from '@/types/NextOfKinEntry';
import dayjs from '@/helpers/dayjs';

interface ConflictApplicant {
  mobile?: string;
  email?: string;
}

const normalizeMobile = (value?: string): string | undefined => {
  if (!value) return undefined;
  const compact = value.replace(/\s+/g, '');
  return compact.startsWith('+61') ? `0${compact.slice(3)}` : compact;
};

const isSameMobile = (value: string | undefined, conflict?: string): boolean => {
  if (!value || !conflict) return true;

  return normalizeMobile(value) !== normalizeMobile(conflict);
};

const isSameEmail = (value: string | undefined, conflict?: string): boolean => {
  if (!value || !conflict) return true;
  return value.trim().toLowerCase() !== conflict.trim().toLowerCase();
};

/**
 * Builds the ERHL next-of-kin validation schema. When `conflictApplicant`
 * is provided (single-applicant loans), mobile and email cannot match
 * the applicant's own contact details.
 */
export const buildErhlNextOfKinSchema = (conflictApplicant?: ConflictApplicant) =>
  yupObject<NextOfKinEntry>({
    relationship: yupString().required().label('Relationship to applicant'),

    otherRelationshipDescription: yupString()
      .when('relationship', {
        is: NextOfKinRelationship.OTHER,
        then: (schema) => schema.required(),
      })
      .label('Relationship description'),

    firstName: yupString().required().label('First name'),
    middleName: yupString().label('Middle name'),
    lastName: yupString().required().label('Last name'),
    preferredName: yupString().label('Preferred name'),
    dateOfBirth: yupString()
      .required()
      .test(
        'is-valid-dob',
        'Date of birth cannot be invalid or a future date',
        (value) => dayjs().format('YYYY-MM-DD') >= (value ?? ''),
      )
      .label('Date of birth'),

    mobile: yupString()
      .required()
      .matches(/^(\+\d{11,15}|04\d{8})$/, '${path} must be valid')
      .test('nok-mobile-not-same-as-applicant', 'Cannot use the same mobile number as the primary applicant', (value) =>
        isSameMobile(value, conflictApplicant?.mobile),
      )
      .label('Mobile'),
    email: yupString()
      .required()
      .email()
      .test('nok-email-not-same-as-applicant', 'Cannot use the same email as the primary applicant', (value) =>
        isSameEmail(value, conflictApplicant?.email),
      )
      .label('Email'),
    confirmEmail: yupString()
      .required()
      .oneOf([ref('email')], 'Emails must match')
      .label('Email confirmation'),

    address: yupAddress().required().label('Residential address'),

    authorityToContact: yupBoolean().required().isTrue('This field must be checked').label('Authority to contact'),
  });

export const erhlNextOfKinSchema = buildErhlNextOfKinSchema();
